import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { paginaEditable } from "@/lib/page-content/catalog";
import { resolverPagina, type FilaBloque } from "@/lib/page-content/resolver";
import type { PageContentResolved } from "@/lib/page-content/tipos";
import { renderBlogMarkdown, renderBlogMarkdownInline } from "@/lib/markdown-render.server";
import {
  deleteSharedEdgeCache,
  readSharedEdgeCache,
  writeSharedEdgeCache,
} from "@/server/site/edge-cache";

// Contenido efectivo (editado sobre el texto por defecto) de las páginas de servicio, resuelto en el
// `beforeLoad` raíz (specs/138). Ojo: la fn admin vive en `src/server/admin/page-content.fns.ts`, NO
// acá — este archivo se importa desde el __root público y su validator de zod viajaría al JS inicial.

const TTL_MS = 60_000;
const memoria = new Map<string, { data: PageContentResolved; expiresAt: number }>();
const claveEdge = (path: string) => `public-page-content:${path}`;

export async function invalidatePageContentCache(path: string) {
  memoria.delete(path);
  await deleteSharedEdgeCache(claveEdge(path));
}

async function filasDe(path: string): Promise<FilaBloque[]> {
  const { data, error } = await supabaseAdmin
    .from("site_page_content")
    .select("block_key, contenido")
    .eq("page_key", path);
  if (error) throw error;
  return (data ?? []) as FilaBloque[];
}

/**
 * Contenido efectivo de una página editable ({} si no lo es). Si no se puede leer la base (tabla ausente,
 * red), se sirve el texto por defecto: el sitio público nunca debe romperse por esto (FR-013).
 */
// Validador manual (no zod) a propósito: los validators viajan al bundle del cliente.
export const getPageContentForPath = createServerFn({ method: "GET" })
  .validator((input: unknown): { path: string } => {
    const path = (input as { path?: unknown } | null)?.path;
    if (typeof path !== "string") throw new Error("path inválido");
    return { path };
  })
  .handler(async ({ data }): Promise<PageContentResolved> => {
    const pagina = paginaEditable(data.path);
    if (!pagina) return {};

    const enMemoria = memoria.get(data.path);
    if (enMemoria && Date.now() < enMemoria.expiresAt) return enMemoria.data;

    const deEdge = await readSharedEdgeCache<PageContentResolved>(claveEdge(data.path));
    if (deEdge) {
      memoria.set(data.path, { data: deEdge, expiresAt: Date.now() + TTL_MS });
      return deEdge;
    }

    let filas: FilaBloque[] = [];
    try {
      filas = await filasDe(data.path);
    } catch (err) {
      console.error("[page-content] se usa el texto por defecto:", err);
    }
    const resuelto = resolverPagina(pagina, filas, {
      bloque: renderBlogMarkdown,
      enLinea: renderBlogMarkdownInline,
    });
    memoria.set(data.path, { data: resuelto, expiresAt: Date.now() + TTL_MS });
    await writeSharedEdgeCache(claveEdge(data.path), resuelto, TTL_MS / 1000);
    return resuelto;
  });
